import React, { useEffect, useState } from 'react';
import LoadingOverlay from 'react-loading-overlay';
import styles from './grant-access.module.scss';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { InfoIcon } from 'src/assets/svg/info-icon';
import AddIcon from 'src/assets/svg/add-icon';
import { ButtonComponent } from 'cp-common-ui-components';
import UTEModalLayout from 'src/components/ute-modal-layout';
import SidePopup from './side-popup';
import {
	AuthenticatedUserStatus,
	availableModulesList,
	IAuthenticatedUserApiResponse,
	IAuthenticatedUserModel,
	IDeleteThirdPartyResponse,
	initialUnAuthenticatedUser,
	ISendLinkToThirdPartyResponse,
	IUnAuthenticatedUserModel,
} from './grant-access.model';
import MinusIcon from 'src/assets/svg/circular-minus-icon';
import PenIcon from 'src/assets/svg/pen-icon';
import SendFiles2DIcon from 'src/assets/svg/send-files-2d-icon';
import { getThirdPartyAuthorizedUsers, deleteThirdPartyUser, sendLinkToThirdPartyUser } from './grant-access-api';
import { UTENotifier } from '../notification/ute-notifier';
import { GRANTACCESSCONSTANTS } from 'src/helper/constants';
import Loader from 'src/components/loader';
import RemoveAccessPopup from './remove-access-popup';
import { logger } from 'src/context/ute-auth-context/auth-provider';

const GrantAccess: React.FC = () => {
	const [showSidePopup, setShowSidePopup] = useState<boolean>(false);
	const [isEdit, setIsEdit] = useState<boolean>(false);
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [userDetails, setUserDetails] = useState<IAuthenticatedUserModel[]>([]);
	const [showRemoveAccessPopup, setShowRemoveAccessPopup] = useState<boolean>(false);
	const [selectedAuName, setSelectedAuName] = useState<string>('');
	const [selectedAuRegistrationId, setSelectedAuRegistrationId] = useState<number>(0);
	const [deleteButtonDisable, setDeleteButtonDisable] = useState<boolean>(false);
	const [selectedUserDetails, setSelectedUserDetails] = useState<IUnAuthenticatedUserModel>(initialUnAuthenticatedUser);
	const [sendLinkButtonDisable, setSendLinkButtonDisable] = useState<{ [key: number]: boolean }>({});

	useEffect(() => {
		fetchThirdPartyAuthorizedUsers();
	}, []);

	const fetchThirdPartyAuthorizedUsers = () => {
		setIsLoading(true);
		getThirdPartyAuthorizedUsers((response: IAuthenticatedUserApiResponse) => {
			setIsLoading(false);
			if (response.error) {
				UTENotifier.Error(GRANTACCESSCONSTANTS.ERROR_FETCHING_USER_DETAILS);
			} else {
				setUserDetails(response.userDetails);
			}
		});
	};

	const toggleSidePopupVisibility = (isVisible: boolean) => {
		setShowSidePopup(isVisible);
	};

	const resetRequiredStates = () => {
		toggleSidePopupVisibility(false);
		setSelectedUserDetails(initialUnAuthenticatedUser);
		setIsEdit(false);
	};

	const handleEditUserDetails = (index: number) => {
		setIsEdit(true);
		setSelectedUserDetails({
			id: userDetails[index]?.id,
			name: userDetails[index]?.name,
			emailAddress: userDetails[index]?.emailAddress,
			countryCode: userDetails[index]?.countryCode,
			mobileNumber: userDetails[index]?.mobileNumber,
			accessibleModules: userDetails[index]?.accessibleModules,
		});
		setShowSidePopup(true);
	};

	const onShowDeleteAccessClick = (auDetail: IAuthenticatedUserModel) => {
		setShowRemoveAccessPopup(true);
		setSelectedAuName(auDetail.name);
		setSelectedAuRegistrationId(auDetail.id);
	};

	const onHideDeleteAccessClick = () => {
		setShowRemoveAccessPopup(false);
		setSelectedAuName('');
		setSelectedAuRegistrationId(0);
	};

	const handleDeleteThirdPartyUser = () => {
		setDeleteButtonDisable(true);
		deleteThirdPartyUser(selectedAuRegistrationId, (deleteThirdPartyResponse: IDeleteThirdPartyResponse) => {
			setDeleteButtonDisable(false);
			if (deleteThirdPartyResponse.error) {
				logger && logger.trackWarning(GRANTACCESSCONSTANTS.ERROR_DELETING_USER);
			} else if (deleteThirdPartyResponse.isSuccess) {
				logger && logger.trackTrace(GRANTACCESSCONSTANTS.SUCCESS_DELETING_USER);
				fetchThirdPartyAuthorizedUsers();
			} else {
				logger && logger.trackWarning(GRANTACCESSCONSTANTS.ERROR_DELETING_USER);
			}
			onHideDeleteAccessClick();
		});
	};

	const handleSendLinkClick = (user: IAuthenticatedUserModel) => {
		setSendLinkButtonDisable((prevState) => ({ ...prevState, [user.id]: true }));
		sendLinkToThirdPartyUser(user.id, (sendLinkToThirdPartyUserResponse: ISendLinkToThirdPartyResponse) => {
			setSendLinkButtonDisable((prevState) => ({ ...prevState, [user.id]: false }));
			if (sendLinkToThirdPartyUserResponse.isSuccess) {
				logger && logger.trackTrace(GRANTACCESSCONSTANTS.SUCCESS_SENDING_LINK);
			} else {
				logger && logger.trackWarning(GRANTACCESSCONSTANTS.ERROR_SENDING_LINK);
			}
		});
	};

	return (
		<LoadingOverlay
			active={isLoading}
			spinner={<Loader />}
			text=''>
			<section
				id={styles.updateEmailSection}
				className='pageModuleContainer'>
				<div className='flex-align-center'>
					<h2 className='pageModuleHeader'>Grant Third-Party Access</h2>
					<OverlayTrigger
						trigger={['hover', 'focus']}
						placement='right'
						rootClose
						overlay={<Tooltip className='clientHubTooltip'>Add up to three users to share your ClientHub</Tooltip>}>
						{({ ref, ...triggerHandler }) => (
							<span
								ref={ref}
								{...triggerHandler}
								style={{ marginBottom: '0.8rem', marginLeft: '6px' }}>
								<InfoIcon />
							</span>
						)}
					</OverlayTrigger>
				</div>
				<div>
					<p
						style={{ marginBottom: '24px' }}
						className='pageModuleDescription'>
						Grant third-party access to your Client Portal with restricted page access
						<br />
						Send a 48-hour Client Portal access link to the added third parties
					</p>
				</div>

				{userDetails?.map((user, index) => (
					<div
						className={styles.thirdPartyUser}
						key={index}>
						<div className={styles.thirdPartyUser_details}>
							<div className='flex-align-center'>
								<div
									className={styles.thirdPartyUser_details__username}
									title={user.name}>
									{user.name}
								</div>
								<div
									className={
										user.status == AuthenticatedUserStatus.Verified
											? styles.thirdPartyUser_details__verifiedStatus
											: styles.thirdPartyUser_details__pendingStatus
									}>
									{AuthenticatedUserStatus[user.status]}
								</div>
							</div>
							<div className={styles.thirdPartyUser_details__modules}>
								{user.accessibleModules
									.split(',')
									.map((module: string) => availableModulesList.filter((x) => x.value == parseInt(module))[0].label)
									.join(', ')}
							</div>
						</div>
						<div className={`${styles.thirdPartyUser_actionBtns} flex-align-center`}>
							{user.status === AuthenticatedUserStatus.Verified && (
								<ButtonComponent
									dataTestAuto='9c505d90-871c-443b-bdd0-4865c67534cf'
									onClick={() => handleSendLinkClick(user)}
									variant={'link'}
									buttonClassName={styles.thirdPartyActionBtns}
									size={'medium'}
									disabled={sendLinkButtonDisable[user.id] || false}>
									<>
										<SendFiles2DIcon />
										Send Link
									</>
								</ButtonComponent>
							)}
							<ButtonComponent
								dataTestAuto='f6d22c88-56bd-46dd-b8fc-9f8c5a932cdc'
								onClick={() => {
									handleEditUserDetails(index);
								}}
								variant={'link'}
								buttonClassName={styles.thirdPartyActionBtns}
								size={'medium'}>
								<>
									<PenIcon />
									Edit
								</>
							</ButtonComponent>
							<ButtonComponent
								dataTestAuto='fd249a5f-cf91-444f-ac5a-8bd1b887f482'
								onClick={() => onShowDeleteAccessClick(user)}
								variant={'link'}
								buttonClassName={styles.thirdPartyActionBtns}
								size={'medium'}>
								<>
									<MinusIcon />
									Delete
								</>
							</ButtonComponent>
						</div>
					</div>
				))}

				<ButtonComponent
					dataTestAuto='ac69fb81-3404-43f6-9426-138d09114245'
					onClick={() => {
						setShowSidePopup(true);
					}}
					variant={'primary'}
					buttonClassName={styles.thirdPartyAddBtn}
					size={'medium'}
					disabled={userDetails?.length >= 3}>
					<span>
						<AddIcon color={`#FFF`} />
						Add
					</span>
				</ButtonComponent>

				{showSidePopup && (
					<UTEModalLayout
						width='500'
						theme='light'
						title={`${isEdit ? 'Edit' : 'Add'} Third-Party User`}
						className='sidePopup'
						hide={() => resetRequiredStates()}
						onClickOutside={() => {}}>
						<SidePopup
							fetchThirdPartyAuthorizedUsers={fetchThirdPartyAuthorizedUsers}
							toggleSidePopupVisibility={toggleSidePopupVisibility}
							userDetails={selectedUserDetails}
							isEdit={isEdit}
							resetRequiredStates={resetRequiredStates}
						/>
					</UTEModalLayout>
				)}

				{showRemoveAccessPopup && (
					<UTEModalLayout
						width='500'
						theme='light'
						title='Revoke Access'
						hide={onHideDeleteAccessClick}
						onClickOutside={() => {}}>
						<RemoveAccessPopup
							auName={selectedAuName}
							auRegistrationId={selectedAuRegistrationId}
							hide={onHideDeleteAccessClick}
							deleteThirdPartyUser={handleDeleteThirdPartyUser}
							deleteButtonDisabled={deleteButtonDisable}
						/>
					</UTEModalLayout>
				)}
			</section>
		</LoadingOverlay>
	);
};

export default GrantAccess;
